import React from 'react';

export default function UserManual({ onClose }) {
  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <h2>User Guide & Documentation</h2>
          <button onClick={onClose} style={styles.closeBtn}>&times;</button>
        </div>
        
        <div style={styles.content}>
          
          {/* SECTION 1: DATA PREPARATION */}
          <section style={styles.section}>
            <h3>1. Preparing Your Data (CSV)</h3>
            <p>To run an analysis, you must upload a <strong>.csv</strong> file. The file must follow this strict format:</p>
            <ul style={styles.list}>
              <li><strong>Row 1:</strong> Headers (Column Names).</li>
              <li><strong>Column 1:</strong> DMU Name (e.g., "Store A", "Hospital 1").</li>
              <li><strong>Input Columns:</strong> Must start with the letter <strong>'I'</strong> (e.g., <em>Input_Labor</em>, <em>I_Cost</em>).</li>
              <li><strong>Output Columns:</strong> Must start with the letter <strong>'O'</strong> (e.g., <em>Output_Sales</em>, <em>O_Revenue</em>).</li>
            </ul>
            
            <div style={styles.codeBlock}>
              <strong>Example CSV Content:</strong><br/>
              DMU, Input_Hours, Input_Cost, Output_Sales, Output_Satisfaction<br/>
              Store A, 100, 5000, 20000, 4.5<br/>
              Store B, 120, 6000, 19000, 4.2<br/>
              Store C, 80, 4000, 18000, 4.8
            </div>
            <p style={{fontSize: '0.9rem', color: '#d9534f'}}>*Note: Ensure there are no empty rows and all data fields contain numbers.</p>
          </section>

          <hr style={styles.divider} />

          {/* SECTION 2: RUNNING ANALYSIS */}
          <section style={styles.section}>
            <h3>2. Running the Analysis</h3>
            <ol style={styles.list}>
              <li><strong>Upload:</strong> Select your CSV file in the "Upload New Dataset" box.</li>
              <li><strong>Select Dataset:</strong> Choose the uploaded file from the dropdown menu.</li>
              <li><strong>Choose Model:</strong>
                <ul>
                  <li><strong>CCR (CRS):</strong> Assumes Constant Returns to Scale. Suitable if size doesn't influence efficiency.</li>
                  <li><strong>BCC (VRS):</strong> Assumes Variable Returns to Scale. Accounts for size differences (fairer for mixing small/large units).</li>
                </ul>
              </li>
              <li><strong>Choose Orientation:</strong>
                <ul>
                  <li><strong>Input-Oriented:</strong> Goal is to reduce inputs while keeping outputs constant (e.g., "Cut costs").</li>
                  <li><strong>Output-Oriented:</strong> Goal is to increase outputs while keeping inputs constant (e.g., "Grow sales").</li>
                </ul>
              </li>
            </ol>
          </section>

          <hr style={styles.divider} />

          {/* SECTION 3: INTERPRETING RESULTS */}
          <section style={styles.section}>
            <h3>3. Interpreting Results</h3>
            <ul style={styles.list}>
              <li><strong>Efficiency Score:</strong>
                <ul>
                  <li><strong>1.00:</strong> Efficient. This unit is a benchmark.</li>
                  <li><strong>&lt; 1.00:</strong> Inefficient. Needs improvement.</li>
                </ul>
              </li>
              <li><strong>Peers (Benchmarks):</strong> The efficient units that the inefficient unit should emulate.</li>
              <li><strong>Slacks:</strong> The "waste." Amount of input that can be reduced <em>beyond</em> the proportional reduction.</li>
              <li><strong>Targets:</strong> The specific Input/Output levels this DMU should aim for to become efficient.</li>
              <li><strong>Scale Efficiency:</strong> 
                <ul>
                  <li>Calculated as <code>CCR Efficiency / BCC Efficiency</code>.</li>
                  <li>If <strong>1.0</strong>: Operating at optimal scale.</li>
                  <li>If <strong>&lt; 1.0</strong>: Suffering from scale inefficiencies (too big or too small).</li>
                </ul>
              </li>
            </ul>
          </section>

        </div>
      </div>
    </div>
  );
}

// Simple inline styles for the modal
const styles = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 1000,
    display: 'flex', justifyContent: 'center', alignItems: 'center'
  },
  modal: {
    backgroundColor: '#fff', width: '90%', maxWidth: '800px', maxHeight: '90vh',
    borderRadius: '8px', display: 'flex', flexDirection: 'column',
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
  },
  header: {
    padding: '20px', borderBottom: '1px solid #eee',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center'
  },
  content: {
    padding: '20px', overflowY: 'auto', lineHeight: '1.6'
  },
  closeBtn: {
    background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer'
  },
  section: { marginBottom: '20px' },
  list: { paddingLeft: '20px', marginTop: '10px' },
  codeBlock: {
    background: '#f4f4f4', padding: '15px', borderRadius: '5px',
    fontFamily: 'monospace', fontSize: '0.9rem', marginTop: '10px', whiteSpace: 'pre-wrap'
  },
  divider: { margin: '20px 0', border: 'none', borderTop: '1px solid #eee' }
};